import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Mail, Shield, Pencil, Trash2, Loader2, CheckCircle, AlertTriangle, Send, Globe } from 'lucide-react';
import { toast } from 'sonner';

const emptyForm = { name: '', domain: '', smtp_host: '', smtp_port: 587, smtp_username: '', smtp_password: '', use_tls: true, use_ssl: false, from_name: '', from_email: '', reply_to: '', daily_limit: 500, hourly_limit: 100, delay_seconds: 2, is_active: false, is_default: false, provider: 'niebieski.net', notes: '' };

export default function SmtpDomainsPage() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testingId, setTestingId] = useState(null);
  const [checkingDns, setCheckingDns] = useState(null);

  const fetchData = useCallback(async () => {
    try { const r = await api.get('/admin/smtp-accounts'); setAccounts(r.data.items); }
    catch (err) { if (err.response?.status === 403) toast.error('Brak uprawnien'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => { setForm({...emptyForm, smtp_host: 'domekkolobrzegzap.6100.niebieski.net'}); setEditId(null); setDialogOpen(true); };
  const openEdit = (a) => { setForm({...a}); setEditId(a.id); setDialogOpen(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editId) { await api.put(`/admin/smtp-accounts/${editId}`, form); toast.success('Konto zaktualizowane'); }
      else { await api.post('/admin/smtp-accounts', form); toast.success('Konto dodane'); }
      setDialogOpen(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Blad'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Usunac konto SMTP?')) return;
    try { await api.delete(`/admin/smtp-accounts/${id}`); toast.success('Usunieto'); fetchData(); }
    catch { toast.error('Blad'); }
  };

  const handleTest = async (id) => {
    if (!testEmail) { toast.error('Podaj email do testu'); return; }
    setTestingId(id);
    try { const r = await api.post(`/admin/smtp-accounts/${id}/test`, { to_email: testEmail }); toast.success(r.data.message); }
    catch (err) { toast.error(err.response?.data?.detail || 'Blad SMTP'); }
    finally { setTestingId(null); }
  };

  const handleCheckDns = async (id) => {
    setCheckingDns(id);
    try { const r = await api.post(`/admin/smtp-accounts/${id}/check-dns`); toast.success(`DNS ${r.data.domain}: SPF=${r.data.spf?.status}, DKIM=${r.data.dkim?.status}, DMARC=${r.data.dmarc?.status}`); fetchData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Blad DNS'); }
    finally { setCheckingDns(null); }
  };

  const DnsIndicator = ({ status }) => {
    if (!status) return <span className="text-xs text-muted-foreground">-</span>;
    return status.status === 'ok' ? <CheckCircle className="h-3.5 w-3.5 text-[hsl(var(--success))]" /> :
      status.status === 'weak' ? <AlertTriangle className="h-3.5 w-3.5 text-[hsl(var(--warning))]" /> :
      <AlertTriangle className="h-3.5 w-3.5 text-[hsl(var(--destructive))]" />;
  };

  if (loading) return <div className="space-y-6"><Skeleton className="h-8 w-48" /><Skeleton className="h-96" /></div>;

  return (
    <div className="space-y-6" data-testid="smtp-domains-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Domeny wysylkowe (SMTP)</h1>
          <p className="text-sm text-muted-foreground mt-1">Zarzadzaj kontami pocztowymi i domenami do wysylki email. Serwer: Niebieski.net</p>
        </div>
        <Button onClick={openCreate} data-testid="smtp-add-button"><Plus className="h-4 w-4 mr-2" />Dodaj domene</Button>
      </div>

      {/* Info card about Niebieski.net */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="py-4 px-5">
          <div className="flex items-start gap-3">
            <Globe className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium">Serwer pocztowy: Niebieski.net</p>
              <p className="text-xs text-muted-foreground mt-1">
                Panel: <a href="http://webas.domekkolobrzegzap.6100.niebieski.net" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">webas.domekkolobrzegzap.6100.niebieski.net</a>
                {' | '}SMTP: domekkolobrzegzap.6100.niebieski.net
                {' | '}Aby dodac domene: Panel &rarr; Domeny &rarr; Dodaj wlasna domene. NS: ns1.tld.pl / ns2.tld.pl
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Test email input */}
      <div className="flex items-center gap-3">
        <Input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="Email do testow (np. twoj@email.pl)" className="max-w-sm bg-card/50" />
      </div>

      {/* Accounts table */}
      <Card className="border-border/50 bg-card/80">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Domena</TableHead>
                <TableHead>Serwer SMTP</TableHead>
                <TableHead>Nadawca</TableHead>
                <TableHead>Limity</TableHead>
                <TableHead>DNS</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-40">Akcje</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {accounts.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Brak kont SMTP. Dodaj pierwsza domene!</TableCell></TableRow>
              ) : accounts.map(a => (
                <TableRow key={a.id} className="hover:bg-muted/30 transition-colors duration-150">
                  <TableCell>
                    <div>
                      <span className="font-medium">{a.domain || '-'}</span>
                      {a.is_default && <Badge variant="outline" className="ml-2 text-[10px] text-primary border-primary/30">Domyslna</Badge>}
                      {a.is_system && <Badge variant="outline" className="ml-2 text-[10px] text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30">Systemowa</Badge>}
                      {a.is_mailing === false && <Badge variant="outline" className="ml-2 text-[10px] text-muted-foreground">Nie do mailingu</Badge>}
                      <p className="text-xs text-muted-foreground">{a.name}</p>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{a.smtp_host}:{a.smtp_port}</TableCell>
                  <TableCell className="text-xs">{a.from_email || a.smtp_username || '-'}</TableCell>
                  <TableCell className="text-xs font-mono text-muted-foreground">{a.hourly_limit}/h {a.daily_limit}/d</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <DnsIndicator status={a.dns_status?.spf} />
                      <DnsIndicator status={a.dns_status?.dkim} />
                      <DnsIndicator status={a.dns_status?.dmarc} />
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={a.is_active ? 'border-[hsl(var(--success))]/30 text-[hsl(var(--success))]' : 'text-muted-foreground'}>
                      {a.is_active ? 'Aktywna' : 'Nieaktywna'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleTest(a.id)} disabled={testingId === a.id} title="Test email">
                        {testingId === a.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleCheckDns(a.id)} disabled={checkingDns === a.id} title="Sprawdz DNS">
                        {checkingDns === a.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Shield className="h-3.5 w-3.5" />}
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(a)}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(a.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editId ? 'Edytuj konto SMTP' : 'Nowa domena wysylkowa'}</DialogTitle>
            <DialogDescription>Skonfiguruj konto pocztowe do wysylki emaili</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label className="text-xs">Nazwa (wewnetrzna)</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="np. Torr.pl - Glowna" /></div>
              <div className="space-y-2"><Label className="text-xs">Domena</Label><Input value={form.domain} onChange={e => setForm({...form, domain: e.target.value})} placeholder="torr.pl" /></div>
            </div>
            <Separator />
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Serwer SMTP</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2 col-span-2"><Label className="text-xs">Host SMTP</Label><Input value={form.smtp_host} onChange={e => setForm({...form, smtp_host: e.target.value})} placeholder="domekkolobrzegzap.6100.niebieski.net" /></div>
              <div className="space-y-2"><Label className="text-xs">Port</Label><Input type="number" value={form.smtp_port} onChange={e => setForm({...form, smtp_port: parseInt(e.target.value) || 587})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label className="text-xs">Login SMTP</Label><Input value={form.smtp_username} onChange={e => setForm({...form, smtp_username: e.target.value})} placeholder="noreply@torr.pl" /></div>
              <div className="space-y-2"><Label className="text-xs">Haslo SMTP</Label><Input type="password" value={form.smtp_password} onChange={e => setForm({...form, smtp_password: e.target.value})} /></div>
            </div>
            <div className="flex gap-6">
              <div className="flex items-center gap-2"><Switch checked={form.use_tls} onCheckedChange={v => setForm({...form, use_tls: v})} /><Label className="text-xs">STARTTLS</Label></div>
              <div className="flex items-center gap-2"><Switch checked={form.use_ssl} onCheckedChange={v => setForm({...form, use_ssl: v})} /><Label className="text-xs">SSL</Label></div>
            </div>
            <Separator />
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Nadawca</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2"><Label className="text-xs">Nazwa nadawcy</Label><Input value={form.from_name} onChange={e => setForm({...form, from_name: e.target.value})} placeholder="Torr.pl" /></div>
              <div className="space-y-2"><Label className="text-xs">Email nadawcy</Label><Input value={form.from_email} onChange={e => setForm({...form, from_email: e.target.value})} placeholder="noreply@torr.pl" /></div>
              <div className="space-y-2"><Label className="text-xs">Reply-To</Label><Input value={form.reply_to} onChange={e => setForm({...form, reply_to: e.target.value})} placeholder="kontakt@torr.pl" /></div>
            </div>
            <Separator />
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Limity wysylki</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2"><Label className="text-xs">Max / godzine</Label><Input type="number" value={form.hourly_limit} onChange={e => setForm({...form, hourly_limit: parseInt(e.target.value) || 100})} /></div>
              <div className="space-y-2"><Label className="text-xs">Max / dzien</Label><Input type="number" value={form.daily_limit} onChange={e => setForm({...form, daily_limit: parseInt(e.target.value) || 500})} /></div>
              <div className="space-y-2"><Label className="text-xs">Delay (sekundy)</Label><Input type="number" value={form.delay_seconds} onChange={e => setForm({...form, delay_seconds: parseInt(e.target.value) || 2})} /></div>
            </div>
            <div className="flex gap-6">
              <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={v => setForm({...form, is_active: v})} /><Label className="text-xs">Aktywna</Label></div>
              <div className="flex items-center gap-2"><Switch checked={form.is_default} onCheckedChange={v => setForm({...form, is_default: v})} /><Label className="text-xs">Domyslna</Label></div>
            </div>
            <div className="space-y-2"><Label className="text-xs">Notatki</Label><Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Uwagi..." /></div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDialogOpen(false)}>Anuluj</Button>
            <Button onClick={handleSave} disabled={saving || !form.domain} data-testid="smtp-form-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editId ? 'Zapisz' : 'Dodaj'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
